/**
 * Collection price-range control. Keeps two labelled sliders within valid bounds,
 * displays localized currency, and submits through Atelier's existing facets form.
 * Dragging only updates the displayed values; Apply performs one results request.
 * Each instance owns abortable listeners so section updates cannot duplicate them.
 * No customer data is read, stored, or logged.
 */
class SBJPriceRange extends HTMLElement {
  connectedCallback() {
    // Shopify morphs existing controls after filtering without reconnecting them.
    // Rebind only when server markup replaces or resets our enhanced controls.
    this.observer ??= new MutationObserver(() => {
      if (!this.querySelector('.sbj-price-range__fallback')?.hidden ||
          this.minSlider !== this.querySelector('[data-price-slider="min"]')) this.initialize();
    });
    this.initialize();
  }

  initialize() {
    this.observer.disconnect();
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.minSlider = this.querySelector('[data-price-slider="min"]');
    this.maxSlider = this.querySelector('[data-price-slider="max"]');
    this.minField = this.querySelector('[data-price-field="min"]');
    this.maxField = this.querySelector('[data-price-field="max"]');
    this.limit = Number(this.dataset.limit);
    if (!this.minSlider || !this.maxSlider || !this.minField || !this.maxField || !Number.isFinite(this.limit)) return;
    this.format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD', maximumFractionDigits: 2
    });
    // Keep real form fields for native availability/sort changes, but show sliders to shoppers.
    this.querySelector('.sbj-price-range__fallback').hidden = true;
    this.querySelector('.sbj-price-range__sliders').hidden = false;
    this.querySelector('[data-price-clear]').hidden = false;
    this.querySelector('[data-price-apply]').type = 'button';
    this.minSlider.disabled = this.maxSlider.disabled = this.limit <= 0;
    this.addEventListener('input', event => {
      if (event.target === this.minSlider || event.target === this.maxSlider) this.sync(event.target);
    }, options);
    this.addEventListener('change', event => {
      // A slider release should not start a second, native form update before Apply.
      if (event.target === this.minSlider || event.target === this.maxSlider) event.stopPropagation();
    }, options);
    this.addEventListener('keydown', event => {
      if (event.target !== this.minSlider && event.target !== this.maxSlider) return;
      const direction = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[event.key];
      if (!direction) return;
      // Arrow keys move one currency unit; Shift moves ten. Home/End retain native behavior.
      event.preventDefault();
      event.target.value = String(Number(event.target.value) + direction * (event.shiftKey ? 1000 : 100));
      this.sync(event.target);
    }, options);
    this.querySelector('[data-price-apply]').addEventListener('click', () => this.apply(), options);
    this.querySelector('[data-price-clear]').addEventListener('click', () => {
      this.minSlider.value = '0';
      this.maxSlider.value = String(this.limit);
      this.sync();
      this.apply();
    }, options);
    this.sync();
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
  }

  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }

  sync(changed) {
    let minimum = Number(this.minSlider.value), maximum = Number(this.maxSlider.value);
    if (minimum > maximum) {
      if (changed === this.minSlider) minimum = maximum;
      else maximum = minimum;
    }
    this.minSlider.value = String(minimum);
    this.maxSlider.value = String(maximum);
    for (const [name, value, slider] of [['min', minimum, this.minSlider], ['max', maximum, this.maxSlider]]) {
      const text = this.format.format(value / 100);
      this.querySelector(`[data-price-output="${name}"]`).textContent = text;
      slider.setAttribute('aria-valuetext', text);
      slider.style.setProperty('--sbj-range-fill', `${this.limit ? value / this.limit * 100 : 0}%`);
    }
    // These attributes tell assistive technology the effective non-crossing bounds.
    this.minSlider.setAttribute('aria-valuemax', String(maximum));
    this.maxSlider.setAttribute('aria-valuemin', String(minimum));
  }

  apply() {
    const minimum = Number(this.minSlider.value), maximum = Number(this.maxSlider.value);
    this.minField.value = minimum === 0 ? '' : (minimum / 100).toFixed(2);
    this.maxField.value = maximum === this.limit ? '' : (maximum / 100).toFixed(2);
    const facets = this.closest('facets-form-component');
    if (typeof facets?.updateFilters === 'function') facets.updateFilters();
    else this.closest('form')?.requestSubmit();
    if (window.Shopify?.designMode) console.debug('[SBJ collection] Price range applied');
  }
}

// Module assets can appear again after a Shopify section replacement.
if (!customElements.get('sbj-price-range')) customElements.define('sbj-price-range', SBJPriceRange);
